import { useCallback, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Dispenser search: resource-only, verbatim results from the reference books (no AI).
 * Instant indexed lookup; an in-flight request is cancelled when a new search starts,
 * and a clear/reset empties everything so the screen never hangs. Auth travels via the
 * httpOnly cookie (credentials included by the axios instance).
 */
export function useSearch(onComplete) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const ctrlRef = useRef(null);

  const cancelInflight = () => {
    if (ctrlRef.current) {
      ctrlRef.current.abort();
      ctrlRef.current = null;
    }
  };

  const clear = useCallback(() => {
    cancelInflight();
    setQuery("");
    setResult(null);
    setError("");
    setLoading(false);
  }, []);

  const runSearch = useCallback(async (rawQuestion, { fresh = false } = {}) => {
    const question = (rawQuestion ?? "").trim();
    if (!question) return;
    cancelInflight();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    setQuery(question);
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post(`/search${fresh ? "?refresh=true" : ""}`, { query: question }, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      setResult(data);
      onComplete?.();
    } catch (err) {
      if (err.code === "ERR_CANCELED" || err.name === "CanceledError" || ctrl.signal.aborted) return;
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Search failed. Please try again.");
    } finally {
      if (ctrlRef.current === ctrl) {
        ctrlRef.current = null;
        setLoading(false);
      }
    }
  }, [onComplete]);

  return { query, setQuery, loading, result, error, runSearch, clear };
}
